import React from "react";

/**
 * Generic React error boundary.
 *
 * The app previously had NO error boundary anywhere, so a single render-time
 * throw (e.g. an unexpected control shape in the AI note view) would unmount the
 * WHOLE React 18 tree and leave the clinician staring at a blank page. Wrapping
 * a risky subtree in this boundary contains the failure to that subtree and
 * shows a fallback instead.
 *
 * Usage:
 *   <ErrorBoundary fallback={<SomethingWentWrong />} resetKeys={[noteJSON]}>
 *     <AINotesView ... />
 *   </ErrorBoundary>
 *
 * `resetKeys` (optional): when any entry changes (shallow compare), a latched
 * error is cleared and children are re-rendered. Pass the data that drives the
 * child (e.g. the note) so a fresh, valid note recovers the panel without a
 * full page refresh.
 */
function arraysDiffer(a = [], b = []) {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return true;
  }
  return false;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log for diagnostics; keep the app alive.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error, info);
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch {
        /* never let the handler itself break the boundary */
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      arraysDiffer(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
